import { Injectable, signal, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppConfig } from '@kildenconfig/app.config';
import { DialogService, DialogVariants } from '@kildencore/services/dialog.service';
import { MapService } from '@kildencore/services/map.service';
import { ThemeLayersService } from '@kildencore/services/theme-layers.service';
import { ApiConfig } from '@kildenconfig/api-config';
import { Kilden3DialogComponent } from '@kildenshared/components/dialog/dialog.component';
import { UploadFileComponent } from '@kildenshared/components/upload-file/upload-file.component';
import { environment } from '../../../environments/environment';
import Collection from 'ol/Collection';
import Feature from 'ol/Feature';
import { GeoJSON, GPX, KML, TopoJSON } from 'ol/format';
import Geometry from 'ol/geom/Geometry';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import Icon from 'ol/style/Icon';
import proj4 from 'proj4';
import { ReplaySubject } from 'rxjs';
import Swal from 'sweetalert2';

declare let JSZip: any;
declare let EPSGUser: any;
declare let EPSG4326: any;
declare let shpLoader: any;
declare let SHPParser: any;
declare let dbfLoader: any;
declare let DBFParser: any;

const MAX_FILE_SIZE = 20971520;

@Injectable({ providedIn: 'root' })
export class UploadService {
  private readonly _dialogService = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _mapService = inject(MapService);
  private readonly _themeLayersService = inject(ThemeLayersService);

  private readonly _uploadedFeatures = new ReplaySubject<number>();
  readonly uploadedFeatures$ = this._uploadedFeatures.asObservable();

  private _dialogRef!: MatDialogRef<Kilden3DialogComponent>;
  private _fileReaders: any[] = [];

  $uploadProgress = signal<number>(0);

  addCustomThemeLayer(features: Feature[], fileName: string): VectorLayer<VectorSource> {
    const newLayerId = this._getUniqueLayerName(AppConfig.IDBOD_USER_UPLOADED);
    const fileNameLayer = this.featuresIntoNewLayer(newLayerId, features, 'Fil: ' + fileName.replace(/\s/, '_'), true);
    this._themeLayersService.registerNewLayer(newLayerId, fileNameLayer);
    return fileNameLayer;
  }

  closeDialog = (msg: String) => this._dialogRef.close(msg);

  featuresIntoNewLayer = (
    layerId: string,
    features: Feature<Geometry>[] | Collection<Feature<Geometry>> | undefined,
    fileName: string,
    purgeExistingLayer = false
  ) => {
    const kml = fileName.endsWith('.kml');
    const style = new Style({
      fill: new Fill({
        color: kml ? [0, 0, 255, 0] : [255, 0, 0, 0],
      }),
      stroke: new Stroke({
        color: kml ? [0, 0, 255, 0.7] : [255, 0, 0, 0.7],
        width: 3,
      }),
      image: new Circle({
        radius: 5,
        fill: new Fill({
          color: [255, 0, 0, 0.7],
        }),
        stroke: new Stroke({
          color: [255, 255, 255, 1],
          width: 2,
        }),
      }),
    });

    // Properly dispose of any existing layers (and their content) using the given ID
    if (purgeExistingLayer) {
      this._themeLayersService.purgeActiveLayer(layerId);
      this._themeLayersService.purgeBankLayer(layerId);
    }

    return new VectorLayer({
      properties: {
        id: layerId,
        label: fileName,
        uploaded: true,
        altitudeMode: 'clampToGround',
      },
      source: new VectorSource({ features: features }),
      style: style,
      zIndex: AppConfig.ZINDEX_UPLOAD,
    });
  };

  openDialog(): MatDialogRef<Kilden3DialogComponent> {
    this._dialogRef = this._matDialog.open(Kilden3DialogComponent, {
      autoFocus: false,
      id: 'uploadDialog',
      minWidth: '300px',
      minHeight: '200px',
      width: '540px',
      hasBackdrop: false,
      data: {
        title: 'Last opp fil (kml, gpx, GeoJSON eller zippet shape)',
        component: UploadFileComponent,
        submitText: 'submitText',
      },
    });

    return this._dialogRef;
  }

  updateReportLayer(features: Feature[], fileName: string): VectorLayer<VectorSource> {
    if (!this._mapService.getLayer(AppConfig.IDBOD_REPORT_UPLOADED)) {
      this._mapService.addLayer(
        AppConfig.IDBOD_REPORT_UPLOADED,
        this.featuresIntoNewLayer(AppConfig.IDBOD_REPORT_UPLOADED, features, fileName)
      );
    } else {
      features.forEach((f: Feature) =>
        this._mapService.getLayer(AppConfig.IDBOD_REPORT_UPLOADED).getSource().addFeature(f)
      );
    }
    this._uploadedFeatures.next(features.length);
    return this._mapService.getLayer(AppConfig.IDBOD_REPORT_UPLOADED);
  }

  uploadFilesToMap(files: FileList): void {
    let uploadError = false;
    const promises: Array<any> = [];
    Array.from(files).forEach(file => {
      this.$uploadProgress.set(0.1); // show abort button
      promises.push(this._readFile(file));
    });
    Promise.allSettled(promises)
      .then(results => {
        this.$uploadProgress.set(100);
        const uploadedLayers: any[] = [];
        results.forEach(r => {
          if (r.status === 'fulfilled') {
            const layer = r.value;
            if (layer) uploadedLayers.push(layer);
          }
        });
        if (uploadedLayers?.length) {
          this._mapService.zoomToUploads(uploadedLayers);
        }
        if (uploadError) this.closeDialog('After reading files');
      })
      .catch(e => {
        uploadError = true;
        throw new Error('Kan ikke laste. Feil med en eller flere filer.');
      });
  }

  ZIP = () => new ZIP({});

  /**
   * If given fileName exists as a ThemeLayer, will increment the fileName until unique.
   * Given fileName 'line.kml' will be returned as 'line(1).kml' or 'line(2).kml' and so on.
   */
  private _getUniqueLayerName(fileName: string): string {
    const allLayers = [
      ...this._themeLayersService.getActiveThemeLayers(),
      ...(this._themeLayersService.getBankLayers() || []),
    ];
    if (!allLayers?.length) {
      return fileName;
    }

    const layerNames: string[] = allLayers.map(l => l.get('id'));
    let name = fileName;
    let counter = 0;

    while (layerNames.includes(name)) {
      const segments = name.split('.');
      let baseName = name;
      let extension = '';
      if (segments.length > 1) {
        extension = segments[segments.length - 1];
        segments.pop();
        baseName = segments.join('.');
      }

      const match = baseName.match(/\(\d+\)/g);

      if (match?.length) {
        baseName = baseName.substring(0, baseName.lastIndexOf(match[0]));
        const countSansParens = match[0].replace('(', '').replace(')', '');
        counter = parseInt(countSansParens);
      }
      counter++;

      name = baseName + '(' + counter + ')' + (extension?.length ? '.' + extension : '');
    }

    return name;
  }

  private errorPopup(text: string) {
    Swal.fire({
      title: 'Feil',
      html: text,
      icon: 'error',
      confirmButtonText: 'OK',
    });
  }

  /**
   * Extract RGBA color from feature's ExtendedData.featureStyle JSON
   * KML ExtendedData often contains: { image_: { color_: [r, g, b, a] } }
   */
  private _extractColorFromFeature(feature: Feature): [number, number, number, number] | null {
    try {
      const extData = feature.get('featureStyle');
      if (!extData) return null;
      const parsed = typeof extData === 'string' ? JSON.parse(extData) : extData;
      const color = parsed?.image_?.color_;
      if (Array.isArray(color) && color.length === 4) {
        return color as [number, number, number, number];
      }
    } catch (e) {
      // Ignore parse errors
    }
    return null;
  }

  /**
   * Extract RGBA from Icon color when available.
   */
  private _extractColorFromIcon(icon: Icon): [number, number, number, number] | null {
    const c: any = icon.getColor?.();
    if (!c) return null;
    if (Array.isArray(c) && c.length >= 3) {
      const a = typeof c[3] === 'number' ? c[3] : 1;
      return [Number(c[0]), Number(c[1]), Number(c[2]), a];
    }
    if (typeof c === 'string') {
      const hex = c.trim();
      const mHex = /^#([0-9a-f]{6})$/i.exec(hex);
      if (mHex) {
        const v = mHex[1];
        return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16), 1];
      }
      const mRgb = /^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\)$/i.exec(hex);
      if (mRgb) {
        return [Number(mRgb[1]), Number(mRgb[2]), Number(mRgb[3]), mRgb[4] !== undefined ? Number(mRgb[4]) : 1];
      }
    }
    return null;
  }

  /**
   * Converts RGBA tuple to 6-char HEX used by svg_color endpoint.
   */
  private _toHexColor(color: [number, number, number, number]): string {
    const [r, g, b] = color;
    const toHex = (v: number) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0');
    return `${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  /**
   * Builds server-side colored SVG URL via svg_color endpoint.
   */
  private _buildColoredSvgUrl(svgSrc: string, color: [number, number, number, number]): string {
    const hex = this._toHexColor(color);
    const absoluteUrl = /^https?:\/\//i.test(svgSrc) ? svgSrc : `${ApiConfig.guiUrl}/${svgSrc.replace(/^\/+/, '')}`;
    // Keep url unencoded to match existing project usage in print serializer.
    return `${ApiConfig.svgUrl}?color=${hex}&url=${absoluteUrl}`;
  }

  /**
   * Clone icon settings and only replace src, preserving placement in 3D.
   */
  private _cloneIconWithSrc(original: Icon, src: string): Icon {
    const o: any = original as any;
    const size: any = o.getSize ? o.getSize() : undefined;
    const anchorRaw: any = o.getAnchor ? o.getAnchor() : [0.5, 0.5];
    const anchorXUnits: any = o.getAnchorXUnits ? o.getAnchorXUnits() : 'fraction';
    const anchorYUnits: any = o.getAnchorYUnits ? o.getAnchorYUnits() : 'fraction';
    const anchor: number[] = Array.isArray(anchorRaw) ? [Number(anchorRaw[0]), Number(anchorRaw[1])] : [0.5, 0.5];

    // OL Icon#getAnchor can return normalized pixel values. Re-normalize when units are fractions.
    if (size && size[0] > 0 && anchorXUnits === 'fraction' && anchor[0] > 1) {
      anchor[0] = anchor[0] / size[0];
    }
    if (size && size[1] > 0 && anchorYUnits === 'fraction' && anchor[1] > 1) {
      anchor[1] = anchor[1] / size[1];
    }

    const iconOptions: any = {
      src,
      crossOrigin: o.getCrossOrigin ? o.getCrossOrigin() : undefined,
      anchor,
      anchorXUnits,
      anchorYUnits,
      anchorOrigin: o.getAnchorOrigin ? o.getAnchorOrigin() : 'top-left',
      offset: o.getOffset ? o.getOffset() : [0, 0],
      offsetOrigin: o.getOffsetOrigin ? o.getOffsetOrigin() : 'top-left',
      size,
      scale: o.getScale ? o.getScale() : 1,
      rotation: o.getRotation ? o.getRotation() : 0,
      rotateWithView: o.getRotateWithView ? o.getRotateWithView() : false,
      opacity: o.getOpacity ? o.getOpacity() : 1,
      displacement: o.getDisplacement ? o.getDisplacement() : [0, 0],
    };
    return new Icon(iconOptions);
  }

  /**
   * Convert same-origin colored SVG URL to PNG data URL for robust Cesium rendering.
   */
  private async _svgUrlToPngDataUrl(url: string, size?: number[]): Promise<string | null> {
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = blobUrl;

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Image load timeout')), 5000);
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Image load failed'));
        };
      });

      const w = size && size[0] ? Math.max(1, Math.round(size[0])) : Math.max(1, img.naturalWidth || 48);
      const h = size && size[1] ? Math.max(1, Math.round(size[1])) : Math.max(1, img.naturalHeight || 48);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(blobUrl);
        return null;
      }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(blobUrl);
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  /**
   * Wait until Icon image has loaded (or failed) so 3D sync can pick it up.
   */
  private _waitForIconReady(icon: Icon, timeoutMs = 8000): Promise<void> {
    return new Promise(resolve => {
      const anyIcon: any = icon as any;
      const start = Date.now();
      const tick = () => {
        const state = anyIcon.getImageState ? anyIcon.getImageState() : undefined;
        if (state === 2 /* LOADED */ || state === 3 /* ERROR */ || Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(tick, 50);
      };
      if (anyIcon.load) {
        anyIcon.load();
      }
      tick();
    });
  }

  private async _displayOnMap(
    f: File,
    format: string,
    fileContent: string
  ): Promise<VectorLayer<VectorSource> | undefined> {
    const gpx = new GPX();
    const is3dActive = this._mapService.$is3dActive();
    const preserveKmlStylesIn3d = environment.preserveKmlStylesIn3d === true;
    // In 3D, KML extracted styles (especially point + text/icon styles) can cause unstable ol-cesium syncing.
    const kml = new KML({ extractStyles: preserveKmlStylesIn3d || !is3dActive });
    const geojson = new GeoJSON();
    const topojson = new TopoJSON();
    const ext2ol: { [key: string]: any } = { geojson, gpx, kml, topojson };
    let uploadedLayer: any;
    if (fileContent) {
      if (format) {
        const ms = this._mapService;
        const fp = is3dActive ? ms.projOn3dInit : ms.getCode();
        try {
          let features: Feature[] = [];
          if (format === 'zip') {
            const shpGeojson = await shp2geojsonAsync(fileContent);
            if (shpGeojson) {
              features = geojson.readFeatures(shpGeojson, { featureProjection: fp });
            }
          } else {
            const readOptions: { dataProjection?: string; featureProjection?: string } = {
              featureProjection: fp,
            };
            const fileProjection = ext2ol[format].readProjection(fileContent);
            if (fileProjection) {
              readOptions['dataProjection'] = fileProjection.getCode();
            }
            features = ext2ol[format].readFeatures(fileContent, readOptions);
          }

          if (!features?.length) {
            return undefined;
          }

          // In 3D + KML: colorize SVG icons from ExtendedData before rendering
          if (is3dActive && format === 'kml' && preserveKmlStylesIn3d) {
            const iconsToWait: Icon[] = [];

            const colorizeIconStyle = async (feature: Feature, s: Style): Promise<void> => {
              const img = s.getImage();
              if (!(img instanceof Icon)) return;

              const src = img.getSrc();
              if (!src || !/\.svg(?:\?|#|$)/i.test(src)) return; // Only SVG icons

              const color = this._extractColorFromIcon(img) || this._extractColorFromFeature(feature);
              if (!color) return; // No color info

              const coloredUrl = this._buildColoredSvgUrl(src, color);
              const size = (img.getSize ? img.getSize() : undefined) as any;
              const pngDataUrl = await this._svgUrlToPngDataUrl(coloredUrl, size);
              const finalSrc = pngDataUrl || coloredUrl;

              // Replace icon source while preserving original placement settings.
              const replacementIcon = this._cloneIconWithSrc(img, finalSrc);
              s.setImage(replacementIcon);
              iconsToWait.push(replacementIcon);
            };

            const iconReplaceTasks: Promise<void>[] = [];

            features.forEach(feature => {
              const raw = feature.getStyle();
              if (!raw) return;
              const resolved = typeof raw === 'function' ? raw(feature, 1) : raw;
              const styles: Style[] = Array.isArray(resolved)
                ? (resolved as Style[])
                : resolved
                  ? [resolved as Style]
                  : [];
              if (typeof raw === 'function' && styles.length > 0) {
                // Freeze resolved style on feature so later renders keep rewritten SVG URL.
                feature.setStyle(styles);
              }
              styles.forEach(s => iconReplaceTasks.push(colorizeIconStyle(feature, s)));
            });

            await Promise.allSettled(iconReplaceTasks);
            await Promise.allSettled(iconsToWait.map(icon => this._waitForIconReady(icon)));
          }

          if (
            this._dialogService.isOpen(DialogVariants.AREA_REPORT) ||
            this._dialogService.isOpen(DialogVariants.FOREST_REPORT) ||
            this._dialogService.isOpen(DialogVariants.SOIL_REPORT)
          ) {
            // Upload as attachment for report
            uploadedLayer = this.updateReportLayer(features, f.name);
          } else {
            // Upload into separate layers and add to ActiveLayers list
            uploadedLayer = this.addCustomThemeLayer(features, f.name);
          }
        } catch (e: any) {
          this.$uploadProgress.set(0);
          throw new Error('Kan ikke laste. Feil med en eller flere filer.');
        }
      }
    }
    return uploadedLayer;
  }

  private _readFile(f: File) {
    return new Promise((resolve, reject) => {
      const format_ = f.name.split('.').pop();
      const format = format_ ? format_.toLowerCase() : '';
      const isZipFormat = format === 'zip';
      const reader = new FileReader();
      this._fileReaders.push(reader); // keep track to be able to abort them

      reader.onprogress = (p: any) => {
        if (p.lengthComputable) {
          this.$uploadProgress.set((p.loaded / p.total) * 100);
        }
      };
      reader.onerror = (e: any) => reject(`Feil ${f.name}`);
      reader.onload = (l: any) => {
        resolve(this._displayOnMap(f, format, l.target.result));
      };

      if (isZipFormat) reader.readAsArrayBuffer(f);
      else reader.readAsText(f, 'UTF-8');
    });
  }
}

function shp2geojson(buffer: any) {
  let shpData, dbfData, prjString, geojson;

  // JSZip v3 removed constructor-with-data. Legacy sync parser is kept only for
  // backward compatibility and should not run in v3 environments.
  if (typeof JSZip?.loadAsync === 'function') {
    return undefined;
  }

  try {
    // Keep ZIP parsing fully synchronous. Mixing sync wrapper with loadAsync causes
    // undefined shp/dbf buffers and can trigger repeated failing parsing attempts.
    const zipInput = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const zip = new JSZip(zipInput);
    const shpFile = zip.file(/\.shp$/i)[0];
    const dbfFile = zip.file(/\.dbf$/i)[0];
    const prjFile = zip.file(/\.prj$/i)[0];

    if (!shpFile || !dbfFile) {
      return undefined;
    }

    shpData = zip.file(shpFile.name).asArrayBuffer();
    dbfData = zip.file(dbfFile.name).asArrayBuffer();
    if (prjFile) {
      prjString = zip.file(prjFile.name).asText();
    }

    EPSGUser = proj4(EPSG4326);
    if (prjString) {
      proj4.defs('EPSGUSER', prjString);
      try {
        EPSGUser = proj4('EPSGUSER');
      } catch (e) {
        console.error('Unsuported Projection: ' + e);
      }
    }

    shpLoader(new SHPParser().parse(shpData, shpFile.name), () => {}, proj4);

    // DBF encoding varies between files. Try common encodings before giving up.
    const dbfEncodings = ['utf-8', 'utf-16', 'windows-1252'];
    for (const enc of dbfEncodings) {
      try {
        const dbfString = new TextDecoder(enc as any).decode(dbfData);
        dbfLoader(
          new DBFParser().parse(dbfData, dbfFile.name, dbfString, enc),
          (result: any) => (geojson = result),
          proj4
        );
        if (geojson) break;
      } catch {
        // try next encoding
      }
    }

    return geojson;
  } catch (e) {
    console.error('shp2geojson failed', e);
    return undefined;
  }
}

async function shp2geojsonAsync(buffer: any) {
  let geojson;

  try {
    const zipInput = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const zip = await JSZip.loadAsync(zipInput);
    const shpFile = zip.file(/\.shp$/i)[0];
    const dbfFile = zip.file(/\.dbf$/i)[0];
    const prjFile = zip.file(/\.prj$/i)[0];

    if (!shpFile || !dbfFile) {
      return undefined;
    }

    const [shpData, dbfData, prjString] = await Promise.all([
      shpFile.async('arraybuffer'),
      dbfFile.async('arraybuffer'),
      prjFile ? prjFile.async('text') : Promise.resolve(undefined),
    ]);

    EPSGUser = proj4(EPSG4326);
    if (prjString) {
      proj4.defs('EPSGUSER', prjString);
      try {
        EPSGUser = proj4('EPSGUSER');
      } catch (e) {
        console.error('Unsuported Projection: ' + e);
      }
    }

    shpLoader(new SHPParser().parse(shpData, shpFile.name), () => {}, proj4);

    const dbfEncodings = ['utf-8', 'utf-16', 'windows-1252'];
    for (const enc of dbfEncodings) {
      try {
        const dbfString = new TextDecoder(enc as any).decode(dbfData);
        dbfLoader(
          new DBFParser().parse(dbfData, dbfFile.name, dbfString, enc),
          (result: any) => (geojson = result),
          proj4
        );
        if (geojson) break;
      } catch {
        // try next encoding
      }
    }

    return geojson;
  } catch (e) {
    console.error('shp2geojsonAsync failed', e);
    return undefined;
  }
}

export type Type = 'arraybuffer' | 'json' | 'text' | 'xml';

// Define a ZIP/SHP format class by subclassing ol/format/GeoJSON:
// @Injectable()
export class ZIP extends GeoJSON {
  constructor(opt_options: any) {
    super(opt_options || {});
  }

  // needed, it will fail otherwise
  override getType() {
    return 'arraybuffer' as Type;
  }

  // loaded zip/buffer has to be read synchronously:
  override readFeatures(source: any, options: any) {
    const geojson = shp2geojson(source);
    if (!geojson) return [];
    return super.readFeatures(geojson, options);
  }
}
